import { Injectable, inject, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

export type Lang = 'en' | 'th';

const LANG_KEY = 'app.language';

function readLocalStorage(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorage(key: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    /* storage disabled */
  }
}

function detectLang(): Lang {
  const stored = readLocalStorage(LANG_KEY);
  if (stored === 'en' || stored === 'th') return stored;
  // Default to English; Thai is opt-in via the switcher (a stored preference always wins).
  return 'en';
}

function syncDocumentLang(lang: Lang): void {
  if (typeof document === 'undefined') return;
  try {
    document.documentElement.lang = lang;
  } catch {
    /* DOM unavailable */
  }
}

@Injectable({ providedIn: 'root' })
export class LanguageStore {
  private readonly transloco = inject(TranslocoService);
  private readonly _language = signal<Lang>(detectLang());

  readonly language = this._language.asReadonly();

  constructor() {
    this.transloco.setActiveLang(this._language());
    syncDocumentLang(this._language());
  }

  setLanguage(lang: Lang): void {
    writeLocalStorage(LANG_KEY, lang);
    this._language.set(lang);
    this.transloco.setActiveLang(lang);
    syncDocumentLang(lang);
  }
}
